/**
 * useLensEntries — the inspector's lens resolution (VIEWS.md §8, S18).
 *
 * Resolves the selected element's computed-inspection values off the shared
 * compile via the engine's `computeLensEntries`: top-level `lens { … }`
 * declarations apply inspector-wide in their space's scope, module-body lens
 * blocks apply to their subtree, and the ACTIVE view (tab-aware effective
 * name; a builder draft counts — its lens clauses are live) adds/overrides by
 * entry name. DetailsPane injects the result into the Sidebar as ONE optional
 * prop; the Sidebar stays a pure view.
 */
import { useMemo } from "react";
import {
  buildClosure,
  buildNodeUniverse,
  computeLensEntries,
  type LensEntryData,
  type StableId,
} from "@archlang/engine";
import { useWorkspaceModel } from "./WorkspaceModelContext.js";
import { useActiveView } from "./ActiveViewContext.js";
import { useEffectiveViewName } from "../views/useEffectiveViewName.js";
import { findView } from "../views/viewRepr.js";

const EMPTY: readonly LensEntryData[] = [];

export function useLensEntries(selectedId: string | null): readonly LensEntryData[] {
  const { resolvedModel, graph } = useWorkspaceModel();
  const effectiveName = useEffectiveViewName();
  const { draftView } = useActiveView();
  // The eval context is a function of the COMPILE, not the selection —
  // universe + closure are O(model) builds, so memo them per model identity
  // rather than rebuilding on every inspector click.
  const ctx = useMemo(
    () =>
      resolvedModel && graph
        ? {
            universe: buildNodeUniverse(resolvedModel),
            graph,
            closure: buildClosure(graph),
            presence: resolvedModel.presence,
          }
        : null,
    [resolvedModel, graph],
  );
  return useMemo(() => {
    if (!selectedId || !resolvedModel || !graph || !ctx) return EMPTY;
    // Board/inspector id → qualified key (StableIds map through the graph
    // index; dotted paths pass through; pseudo-ids resolve to no subject).
    const raw = selectedId.startsWith("#") ? selectedId.slice(1) : selectedId;
    const byId = graph.modulesById.get(raw as StableId);
    const key = byId ? (graph.pathOfModule.get(byId)?.join(".") ?? raw) : raw;
    const view =
      draftView ??
      (effectiveName ? (findView(resolvedModel.views, effectiveName) ?? null) : null);
    try {
      return computeLensEntries(resolvedModel, view, key, ctx);
    } catch (e) {
      // A pathological lens expression must degrade to "no entries", never
      // unmount the inspector (this runs in a render useMemo). The engine
      // already absorbs per-entry evaluator errors; this is the catch-all for
      // anything that still escapes.
      console.debug("[lens] evaluation failed:", e);
      return EMPTY;
    }
  }, [selectedId, resolvedModel, graph, ctx, effectiveName, draftView]);
}
